<template>
  <div>
    <breadcrumb :items="breadcrumbs" />

    <v-container fluid>
      <div class="d-flex align-center justify-space-between mt-6 mb-4">
        <h2 class="font-weight-bold mb-6">{{ pageTitle }}</h2>

        <v-btn
          outlined
          class="mr-2 mb-2"
          :to="{ name: 'loanApplicationDetails', params: { id: applicationIdParam } }"
          style="border-color: #00BD73; color: #00BD73; background-color: white;"
        >
          {{ $t('finance.loanApplications.back') }}
        </v-btn>
      </div>

      <!-- Disclaimer (NewWarningBox-style like Additional Information) -->
      <v-card outlined class="pa-2 my-4 additional-info-card">
        <v-row class="additional-info-header" align="center">
          <v-col cols="auto" class="mx-3 d-flex align-center">
            <v-icon class="mr-2" color="warning">mdi-alert</v-icon>
            <h2 class="additional-info-heading mb-0">{{ $t('finance.loanApplications.disclaimerTitle') }}</h2>
          </v-col>
        </v-row>

        <div class="additional-info-body disclaimer-body">
          <div v-if="selectedReport === 'yield'">
            <p class="mb-0">
              {{ $t('finance.loanApplications.farmReportsYieldUncertaintyIntro') }}
            </p>
            <ol class="no-indent-list">
              <li>{{ $t('finance.loanApplications.farmReportsYieldBullet1') }}</li>
              <li>{{ $t('finance.loanApplications.farmReportsYieldBullet2') }}</li>
              <li>{{ $t('finance.loanApplications.farmReportsYieldBullet3') }}</li>
              <li>{{ $t('finance.loanApplications.farmReportsYieldBullet4') }}</li>
            </ol>
          </div>

          <div v-else-if="selectedReport === 'land'">
            <p class="mb-0">{{ $t('finance.loanApplications.farmReportsLandAiDisclaimer') }}</p>
          </div>

          <div v-else>
            <p class="mb-0">
              {{ $t('finance.loanApplications.farmReportsSoilDisclaimer') }}
            </p>
          </div>
        </div>
      </v-card>

      <!-- Farm Details (shared) -->
      <v-card elevation="0" class="pa-6">
        <div class="mb-6">
          <div class="d-flex align-center mb-4">
            <h3 class="font-weight-bold mr-2">
              {{ $t('finance.loanApplications.farmDetails') }}
            </h3>
            <v-tooltip top color="#00BD73" max-width="300">
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  class="ml-1"
                  size="20"
                  style="color: #00BD73; cursor: pointer;"
                  v-bind="attrs"
                  v-on="on"
                >
                  mdi-alert-circle
                </v-icon>
              </template>
              <span>{{ $t('finance.loanApplications.farmDetails') }}</span>
            </v-tooltip>
          </div>

          <v-row>
            <!-- Text details (3 columns * 2 rows) -->
            <v-col cols="12" md="9">
              <v-row>
                <v-col cols="12" md="4">
                  <div class="view-block">
                    <div class="d-flex align-center">
                      <label class="view-label">{{ $t('finance.loanApplications.farm') }}</label>
                      <v-tooltip top color="#00BD73" max-width="300">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            class="ml-2"
                            size="20"
                            style="color: #00BD73; cursor: pointer;"
                            v-bind="attrs"
                            v-on="on"
                          >
                            mdi-alert-circle
                          </v-icon>
                        </template>
                        <span>{{ $t('finance.loanApplications.farm') }}</span>
                      </v-tooltip>
                    </div>
                    <div class="view-value">{{ application.farmName }}</div>
                  </div>
                </v-col>

                <v-col cols="12" md="4">
                  <div class="view-block">
                    <div class="d-flex align-center">
                      <label class="view-label">{{ $t('finance.loanApplications.area') }}</label>
                      <v-tooltip top color="#00BD73" max-width="300">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            class="ml-2"
                            size="20"
                            style="color: #00BD73; cursor: pointer;"
                            v-bind="attrs"
                            v-on="on"
                          >
                            mdi-alert-circle
                          </v-icon>
                        </template>
                        <span>{{ $t('finance.loanApplications.area') }}</span>
                      </v-tooltip>
                    </div>
                    <div class="view-value">{{ application.area }}</div>
                  </div>
                </v-col>

                <v-col cols="12" md="4">
                  <div class="view-block">
                    <div class="d-flex align-center">
                      <label class="view-label">{{ $t('finance.loanApplications.provinceState') }}</label>
                      <v-tooltip top color="#00BD73" max-width="300">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            class="ml-2"
                            size="20"
                            style="color: #00BD73; cursor: pointer;"
                            v-bind="attrs"
                            v-on="on"
                          >
                            mdi-alert-circle
                          </v-icon>
                        </template>
                        <span>{{ $t('finance.loanApplications.provinceState') }}</span>
                      </v-tooltip>
                    </div>
                    <div class="view-value">{{ application.provinceState }}</div>
                  </div>
                </v-col>

                <v-col cols="12" md="4">
                  <div class="view-block">
                    <div class="d-flex align-center">
                      <label class="view-label">{{ $t('finance.loanApplications.regionDistrict') }}</label>
                      <v-tooltip top color="#00BD73" max-width="300">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            class="ml-2"
                            size="20"
                            style="color: #00BD73; cursor: pointer;"
                            v-bind="attrs"
                            v-on="on"
                          >
                            mdi-alert-circle
                          </v-icon>
                        </template>
                        <span>{{ $t('finance.loanApplications.regionDistrict') }}</span>
                      </v-tooltip>
                    </div>
                    <div class="view-value">{{ application.regionDistrict }}</div>
                  </div>
                </v-col>

                <v-col cols="12" md="4">
                  <div class="view-block">
                    <div class="d-flex align-center">
                      <label class="view-label">{{ $t('finance.loanApplications.villageTownCity') }}</label>
                      <v-tooltip top color="#00BD73" max-width="300">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            class="ml-2"
                            size="20"
                            style="color: #00BD73; cursor: pointer;"
                            v-bind="attrs"
                            v-on="on"
                          >
                            mdi-alert-circle
                          </v-icon>
                        </template>
                        <span>{{ $t('finance.loanApplications.villageTownCity') }}</span>
                      </v-tooltip>
                    </div>
                    <div class="view-value">{{ application.villageTownCity }}</div>
                  </div>
                </v-col>

                <v-col cols="12" md="4">
                  <div class="view-block">
                    <div class="d-flex align-center">
                      <label class="view-label">{{ $t('finance.loanApplications.address') }}</label>
                      <v-tooltip top color="#00BD73" max-width="300">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            class="ml-2"
                            size="20"
                            style="color: #00BD73; cursor: pointer;"
                            v-bind="attrs"
                            v-on="on"
                          >
                            mdi-alert-circle
                          </v-icon>
                        </template>
                        <span>{{ $t('finance.loanApplications.address') }}</span>
                      </v-tooltip>
                    </div>
                    <div class="view-value">{{ application.address }}</div>
                  </div>
                </v-col>
              </v-row>
            </v-col>

            <!-- Map column -->
            <v-col cols="12" md="3">
              <div
                class="view-block map-wrapper"
                @click="showMapDialog = true"
              >
                <GmapMap
                  :center="mapCenter"
                  :zoom="14"
                  map-type-id="satellite"
                  style="width: 100%; height: 180px; border-radius: 5px; overflow: hidden;"
                >
                  <GmapMarker :position="mapCenter" />
                </GmapMap>
                <div class="map-caption mt-2 d-flex align-center">
                  <span class="map-text">
                    {{ $t('finance.loanApplications.tapTheMapToExpandIt') }}
                  </span>
                  <v-icon small class="ml-1">mdi-magnify-plus-outline</v-icon>
                </div>
              </div>
            </v-col>
          </v-row>
        </div>

      <!-- Report Switcher -->
      <v-row class="farm-reports-row mb-6" no-gutters>
        <v-col cols="12" md="4" class="px-2">
          <v-btn
            block
            small
            outlined
            :color="selectedReport === 'yield' ? '#808080' : '#00BD73'"
            :style="selectedReport === 'yield' ? 'border-color:#808080;' : 'opacity:0.55;'"
            @click="setReport('yield')"
          >
            {{ $t('finance.loanApplications.yieldPredictionReport') }}
          </v-btn>
        </v-col>
        <v-col cols="12" md="4" class="px-2">
          <v-btn
            block
            small
            outlined
            :color="selectedReport === 'soil' ? '#808080' : '#00BD73'"
            :style="selectedReport === 'soil' ? 'border-color:#808080;' : 'opacity:0.55;'"
            @click="setReport('soil')"
          >
            {{ $t('finance.loanApplications.soilAnalysisReport') }}
          </v-btn>
        </v-col>
        <v-col cols="12" md="4" class="px-2">
          <v-btn
            block
            small
            outlined
            :color="selectedReport === 'land' ? '#808080' : '#00BD73'"
            :style="selectedReport === 'land' ? 'border-color:#808080;' : 'opacity:0.55;'"
            @click="setReport('land')"
          >
            {{ $t('finance.loanApplications.landSuitabilityReport') }}
          </v-btn>
        </v-col>
      </v-row>

        <v-divider class="my-6" />

      <!-- Report Details (bottom content) -->
      <yield-prediction-report-details
        v-if="selectedReport === 'yield'"
        :application="application"
      />
      <soil-analysis-report-details
        v-else-if="selectedReport === 'soil'"
        :application="application"
      />
      <land-suitability-report-details
        v-else
        :application="application"
      />
      </v-card>

      <LocationDialog
        v-model="showMapDialog"
        :farm-name="application.farmName"
        :farmer-name="application.applicantName"
        :country="application.provinceState"
        :area-ha="application.area"
        :initial-latitude="mapLat"
        :initial-longitude="mapLng"
      />
    </v-container>
  </div>
</template>

<script>
import Breadcrumb from '@/components/core/Breadcrumb';
import LocationDialog from './components/LocationDialog.vue';

import YieldPredictionReportDetails from './components/YieldPredictionReportDetails.vue';
import SoilAnalysisReportDetails from './components/SoilAnalysisReportDetails.vue';
import LandSuitabilityReportDetails from './components/LandSuitabilityReportDetails.vue';

export default {
  name: 'FarmReports',
  components: {
    breadcrumb: Breadcrumb,
    LocationDialog,
    YieldPredictionReportDetails,
    SoilAnalysisReportDetails,
    LandSuitabilityReportDetails,
  },
  data() {
    return {
      applicationIdParam: null,
      selectedReport: 'yield',
      showMapDialog: false,
      mapLat: 0,
      mapLng: 0,
      // For now, reuse the same sample farm fields already used in loan-application `Details.vue`.
      // Later this can be replaced with real API data.
      application: {
        applicantName: 'Ahmed Santoso',
        farmName: 'Brown Coffee Farm',
        area: '4 acre',
        provinceState: 'Jawa Barat',
        regionDistrict: 'Kecamatan',
        villageTownCity: 'Kabupaten',
        address: 'Jl. Raya Sawah No. 45, Desa Sukamaju, Kecamatan Ciamis, Provinsi Jawa Barat',
      },
    };
  },
  computed: {
    reportLabel() {
      const map = {
        yield: this.$t('finance.loanApplications.yieldPredictionReport'),
        soil: this.$t('finance.loanApplications.soilAnalysisReport'),
        land: this.$t('finance.loanApplications.landSuitabilityReport'),
      };
      return map[this.selectedReport] || map.yield;
    },
    pageTitle() {
      return this.reportLabel;
    },
    mapCenter() {
      return {
        lat: this.mapLat || 0,
        lng: this.mapLng || 0,
      };
    },
    breadcrumbs() {
      return [
        {
          text: this.$t('finance.loanApplications.financeManagement'),
          disabled: false,
          href: '/finance-management',
          exact: true,
        },
        {
          text: this.$t('finance.loanApplications.loanApplications'),
          disabled: false,
          href: '/finance-management/loan-applications',
        },
        {
          text: this.applicationIdParam,
          disabled: false,
          href: `/finance-management/loan-applications/${this.applicationIdParam}`,
        },
        {
          text: this.reportLabel,
          disabled: true,
        },
      ];
    },
  },
  mounted() {
    this.applicationIdParam = this.$route.params.id;

    const q = this.$route.query.report;
    if (q === 'yield' || q === 'soil' || q === 'land') {
      this.selectedReport = q;
    }

    // Initialize map with current location (fallback to Jakarta).
    if (navigator && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          this.mapLat = pos.coords.latitude;
          this.mapLng = pos.coords.longitude;
        },
        () => {
          this.mapLat = -6.2088;
          this.mapLng = 106.8456;
        },
      );
    } else {
      this.mapLat = -6.2088;
      this.mapLng = 106.8456;
    }
  },
  methods: {
    setReport(reportType) {
      this.selectedReport = reportType;
      this.$router
        .replace({
          name: 'loanApplicationFarmReports',
          params: { id: this.applicationIdParam },
          query: { report: reportType },
        })
        .catch(() => {});
    },
  },
};
</script>

<style scoped>
.view-label {
  font-size: 14px;
  color: #555;
  margin-bottom: 4px;
  display: block;
  font-weight: 400;
}

.view-value {
  font-weight: bold;
  font-size: 1rem;
  color: #333;
}

.view-block {
  margin-bottom: 8px;
}

.map-caption .map-text {
  font-size: 12px;
  color: #000;
}

.map-wrapper {
  cursor: pointer;
}

.farm-reports-row .px-2 {
  padding-left: 6px !important;
  padding-right: 6px !important;
}

/* Disclaimer box (NewWarningBox-style, matching Details.vue) */
.additional-info-card {
  background-color: #fff8e1 !important;
}

.additional-info-header {
  cursor: pointer;
}

.additional-info-heading {
  color: #000;
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
}

.additional-info-body {
  margin: 12px 16px 16px;
}

/* User requested: disclaimer text 50% smaller */
.disclaimer-body {
  font-size: 0.58em;
  line-height: 1.45;
}

/* Ensure yield disclaimer list has no left indentation */
.no-indent-list {
  padding-left: 0 !important;
  margin-left: 0 !important;
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  list-style-position: inside;
}
</style>

